import React, { useEffect, useState, useRef } from "react";
import { cn } from "../lib/helpers";

import * as styles from "../styles/components/video.module.css";

interface VideoAsset {
  asset?: {
    url?: string;
    originalFilename?: string;
  }
}

interface VideoProps {
  webm?: VideoAsset;
  mp4?: VideoAsset;
  delay?: boolean;
}

const Video: React.FC<VideoProps> = ({ webm, mp4, delay }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [inView, setInView] = useState(false);

  // Build URLs once
  if (!webm?.asset || !mp4?.asset) return null;
  const webmUrl = `${webm.asset.url}/${webm.asset.originalFilename}/`;
  const mp4Url  = `${mp4.asset.url}/${mp4.asset.originalFilename}/`;

  useEffect(() => {
    const vid = ref.current;
    if (!vid) return;

    // never preload anything until inView
    vid.preload = "none";

    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.05 }
    );
    observer.observe(vid);

    return () => {
      observer.disconnect();
    };
  }, []);

  // When inView flips to true → inject sources & play
  useEffect(() => {
    const vid = ref.current;
    if (!vid) return;

    if (inView && !hasLoaded) {
      // inject our sources
      vid.innerHTML = `
        <source src="${webmUrl}" type="video/webm">
        <source src="${mp4Url}"  type="video/mp4">
      `;
      // give browser the go-ahead
      vid.load();
      setHasLoaded(true);

      // optional delay
      setTimeout(() => vid.play().catch(() => {/* swallow */}), delay ? 1000 : 0);
    }

    if (!inView && hasLoaded) {
      // tear it down to free memory
      vid.pause();
      vid.removeAttribute("src");
      Array.from(vid.querySelectorAll("source")).forEach(el => el.remove());
      vid.load();
      setHasLoaded(false);
    }
  }, [inView, hasLoaded, webmUrl, mp4Url, delay]);

  return (
    <video
      ref={ref}
      loop
      muted
      playsInline
      className={cn(styles.root, inView && hasLoaded && styles.show)}
      // no src here!
    />
  );
};

export default Video;
